import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import { Condition } from 'types/GlobalKpiOption'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'

import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { NUMERIC_FILTER_CONDITIONS } from 'utils/functions'

import ConditionSelect from './ConditionSelect'
import { DEFAULT_CONDITIONS } from './lib'

// currently only supporting a subset of Condition
type TFilterItemCondition =
  | Condition.EQ
  | Condition.NE
  | Condition.IS_NOT_NULL
  | Condition.IS_NULL
  | Condition.GT
  | Condition.LT

interface Props {
  attributeOptions: Option[]
  attributeSelected: Option | null
  conditionSelected: TFilterItemCondition
  disabled?: boolean
  disablePadding?: boolean
  editable?: boolean
  numeric?: boolean
  onChangeAttribute: (id: string) => void
  onChangeCondition: (value: TFilterItemCondition) => void
  onChangeValue: (value: string[]) => void
  onDelete?: () => void
  sx?: SxProps
  valueOptions: Option[]
  valueSelected: string[]
  variant?: 'filled' | 'outlined' | 'transparent'
}

const NO_VALUES_CONDITIONS = [Condition.IS_NOT_NULL, Condition.IS_NULL]

const FilterItem = (props: Props) => {
  const {
    attributeOptions,
    attributeSelected,
    conditionSelected,
    disabled,
    disablePadding,
    editable,
    numeric,
    onChangeAttribute,
    onChangeCondition,
    onChangeValue,
    onDelete,
    sx,
    valueOptions,
    valueSelected,
    variant
  } = props

  const { t, translateEnum } = useTranslation()
  const [showDetails, setShowDetails] = useState<boolean>(false)

  // numeric input is committed on blur or Enter, so that a value is not
  // saved on every keystroke
  const [numericDraft, setNumericDraft] = useState<string | null>(null)

  const commitNumericDraft = () => {
    if (numericDraft === null) {
      return
    }

    onChangeValue(numericDraft === '' ? [] : [numericDraft])
    setNumericDraft(null)
  }

  const hideInputValues = NO_VALUES_CONDITIONS.includes(conditionSelected)

  if (!editable)
    return (
      <>
        <Tooltip title="Klicka för att förstora">
          <Chip
            clickable={true}
            label={
              <>
                <b>{attributeSelected?.title}</b>{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {translateEnum('conditions', conditionSelected)}
                </Box>{' '}
                {hideInputValues ? null : <b>{valueSelected.join(', ')}</b>}
              </>
            }
            onClick={() => setShowDetails(true)}
            size="small"
            sx={{
              ['&:not(:last-child)']: {
                mr: 1,
                mb: variant === 'transparent' ? 1 : undefined
              },
              ...sx
            }}
          />
        </Tooltip>

        <SimpleDialog onClose={() => setShowDetails(false)} open={showDetails}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <Chip label={attributeSelected?.title} />
              <Box component="span">
                {translateEnum('conditions', conditionSelected)}
              </Box>
            </Box>
            {hideInputValues ? null : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {valueSelected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
          </Box>
        </SimpleDialog>
      </>
    )

  return (
    <Box
      sx={{
        border: variant === 'outlined' ? '1px solid' : undefined,
        borderColor: 'divider',
        bgcolor: variant === 'filled' ? 'background.paper' : undefined,
        borderRadius: 1,
        display: 'flex',
        gap: 1,
        p: disablePadding ? 0 : 1,
        ['&:not(:last-child)']: {
          mb: 1
        },
        ...sx
      }}
    >
      <SelectSearch
        closeOnSelect={true}
        color="primary"
        disabled={disabled}
        multiple={false}
        noGutter={true}
        onChange={(option) =>
          option ? onChangeAttribute(option.id as string) : {}
        }
        options={attributeOptions}
        selected={attributeSelected}
        sx={{ flex: 3 }}
      />
      <ConditionSelect
        conditions={numeric ? NUMERIC_FILTER_CONDITIONS : DEFAULT_CONDITIONS}
        selected={conditionSelected}
        onChange={onChangeCondition}
      />
      {hideInputValues ? null : numeric ? (
        <TextField
          disabled={disabled}
          noGutter={true}
          onBlur={commitNumericDraft}
          onChange={(event) => setNumericDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commitNumericDraft()
            }
          }}
          size="small"
          sx={{ flex: 5 }}
          type="number"
          value={numericDraft ?? valueSelected[0] ?? ''}
        />
      ) : (
        <SelectSearch
          disabled={disabled}
          multiple={true}
          noGutter={true}
          onChange={(options: Option[]) =>
            onChangeValue(options.map((opt) => opt.id as string))
          }
          options={valueOptions}
          selected={valueSelected.map((value) => {
            return {
              id: value,
              title: value
            }
          })}
          sx={{ flex: 5 }}
        />
      )}

      {onDelete ? (
        <Box sx={{ flexShrink: 0, ml: -0.5, py: 0.5 }}>
          <Tooltip title={t('global', 'delete')}>
            <IconButton onClick={onDelete} size="small">
              <Icon fontSize="small" name="RemoveCircleOutlineOutlined" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  )
}

FilterItem.displayName = 'FilterItem'
export default FilterItem
